import { injectable } from 'inversify';

import { IPlayerRepository } from '@/domain/repositories/player.repository.interface';
import { PlayerInfo, PlayerTransactionsData, Result } from '@/entities';
import { mapToPlayerAvailabilityDto } from '@/infrastructure/mapper/mapToPlayerAvailabilityDto';
import { getPlayerAvailability, getPlayerStats, getPlayerTransactions, getTeamPlayers } from '@/infrastructure/network/data/player';
import { listTeamLeaguesPlayers } from '@/infrastructure/network/data/team-leagues-players';
import { PlayerAvailabilityDto } from '@/infrastructure/network/dto/playerAvailabilityDto';
import { PlayerStatsDto } from '@/infrastructure/network/dto/PlayerStatsDto';
import { TeamPlayerInfo } from '@/lib/types';
import { SearchParams } from '@/entities/SearchParams';

@injectable()
export class PlayerRepository implements IPlayerRepository {
	async getPlayerAvailability(organizationUuid: string, teamLeagueUuid: string, playerUuid: string): Promise<Result<PlayerAvailabilityDto[]>> {
		const result = await getPlayerAvailability({ organizationUuid, teamLeagueUuid, playerUuid });
		if (result.statusCode === 200) {
			return { data: mapToPlayerAvailabilityDto(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getPlayerStats(organizationUuid: string, teamLeagueUuid: string, seasonUuid: string, playerUuid: string): Promise<Result<PlayerStatsDto>> {
		const result = await getPlayerStats({ organizationUuid, teamLeagueUuid, seasonUuid, playerUuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getAllTeamLeaguePlayers(organizationUuid: string, teamLeagueUuid: string, team_uuid: string): Promise<Result<PlayerInfo[]>> {
		const result = await listTeamLeaguesPlayers({
			organizationUuid: organizationUuid,
			teamLeagueUuid: teamLeagueUuid,
			searchParams: { team_uuid: team_uuid }
		});
		if (result.statusCode === 200) {
			return { data: result.data.results.teamLeaguePlayersInfo, statusCode: result.statusCode };
		} else {
			return { data: [], statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
	async getPlayerTransactions(organizationUuid: string, playerUuid: string): Promise<Result<PlayerTransactionsData[]>> {
		const result = await getPlayerTransactions({ organizationUuid, playerUuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async listTeamPlayers(
		organizationUuid: string,
		teamLeagueUuid: string,
		teamUuid: string,
		searchParams?: SearchParams
	): Promise<Result<TeamPlayerInfo[]>> {
		const result = await getTeamPlayers({ organizationUuid, teamLeagueUuid, teamUuid, searchParams });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
}
